import React from 'react';
import { motion } from 'framer-motion';
import { useFeatureFlag } from '../hooks/useFeatureFlags';
import { useFeatureGateCacheCleanup } from '../hooks/useFeatureGateCacheCleanup';
import { FeatureDisabledMessage } from './feature-flags/FeatureDisabledMessage';
import { GenericDenialMessage } from './feature-flags/GenericDenialMessage';
import { FeatureNotFoundMessage } from './feature-flags/FeatureNotFoundMessage';
import LegacyTeaserPage from '../pages/TeaserPage'; // Import rich legacy teaser page

// Mapping between feature flag keys and teaserContent.ts keys
// This allows the feature flag system to use the rich legacy teaser pages
const FEATURE_FLAG_TO_TEASER_CONTENT: Record<string, string> = {
  'portfolio_command_center': 'Portfolio Command Center',
  'portfolio_optimization': 'Portfolio Optimization',
  'portfolio_forecast': 'Portfolio Forecast',
  'strategy_details': 'Strategy Details',
  'advanced_charts': 'Advanced Charts',
  'export_functionality': 'Export Functionality',
  'real_time_notifications': 'Real-Time Notifications',
  'admin_panel': 'Admin Panel',
  'live_trades': 'Live Trades',
  'closed_trades': 'Closed Trades',
  'bot_maintenance': 'Bot Maintenance',
  // Add more mappings as needed
};

interface FeatureGateProps {
  featureKey: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showTeaser?: boolean;
  loadingComponent?: React.ReactNode;
  className?: string;
  queryKeysToInvalidate?: Array<string | unknown[]>; // Query keys to clear when feature disabled
}

/**
 * FeatureGate component - conditionally renders content based on feature flag access
 * Enhanced with:
 * - Seamless loading: Renders children immediately, letting pages show their own skeletons
 * - Transparent access checking: Feature flag verification happens in background
 * - Multi-layer cache cleanup when feature becomes disabled
 * - Comprehensive denial handling for all scenarios
 * - Smart loading detection: only shows loading when truly no data exists
 * - Integration with legacy TeaserPage for rich teaser content
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({
  featureKey,
  children,
  fallback,
  showTeaser = true,
  loadingComponent,
  className,
  queryKeysToInvalidate = []
}) => {
  const { 
    isEnabled, 
    isLoading, 
    // isFetching, // Available for future use (e.g., subtle refresh indicator)
    hasData,
    error,
    reason, 
    displayName,
    minimumRole 
  } = useFeatureFlag(featureKey);

  // Clean up cached data when feature becomes disabled
  useFeatureGateCacheCleanup({
    isEnabled,
    featureKey,
    queryKeysToInvalidate
  });

  // ✅ FLICKER FIX: Only show loading when we have NO data
  // Don't show loading during background refetches with cached data
  // This prevents the "Checking access..." flash when navigating back to cached pages
  const shouldShowLoading = isLoading && !hasData;

  // Debug logging
  if (process.env.NODE_ENV === 'development' && shouldShowLoading) {
    console.log(`[FeatureGate:${featureKey}] 🔄 Transparent wrapper mode - rendering children during access check`, {
      isLoading,
      hasData,
      shouldShowLoading,
      willShowPageSkeleton: !loadingComponent
    });
  }

  // ✅ SEAMLESS LOADING: Render children immediately - let pages show their skeletons
  // This eliminates the "random empty box" and provides continuous loading experience
  if (shouldShowLoading) {
    // If custom loading component provided, use it
    // Otherwise, render children (page will handle its own loading state)
    return loadingComponent ? <>{loadingComponent}</> : <>{children}</>;
  }

  // ✅ ERROR HANDLING: Handle feature flag API failures gracefully
  if (error && !hasData) {
    return (
      <GenericDenialMessage
        featureName={displayName || featureKey}
        reason="Unable to verify feature access. Please try again."
      />
    );
  }

  if (isEnabled) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={className}
      >
        {children}
      </motion.div>
    );
  }

  // Feature is disabled - handle all denial cases
  const denialReason = reason || 'Access denied';
  
  // Case 1: Insufficient role - show upgrade teaser
  if (showTeaser && denialReason.includes('Insufficient role')) {
    // Map feature key to teaser content key for rich teaser pages
    const teaserContentKey = FEATURE_FLAG_TO_TEASER_CONTENT[featureKey];
    
    // Use legacy TeaserPage for rich content with demo components
    return (
      <LegacyTeaserPage
        featureName={teaserContentKey || displayName || featureKey}
        minimumRequiredRole={minimumRole}
      />
    );
  }
  
  // Case 2: Feature globally disabled - show maintenance message
  if (denialReason.includes('globally disabled')) {
    return (
      <FeatureDisabledMessage
        featureName={displayName || featureKey}
        reason="This feature is temporarily unavailable"
        canContact={true}
      />
    );
  }
  
  // Case 3: Feature not found - show error
  if (denialReason.includes('not found')) {
    return (
      <FeatureNotFoundMessage
        featureKey={featureKey}
      />
    );
  }
  
  // Case 4: Custom fallback provided
  if (fallback) {
    return <>{fallback}</>;
  }
  
  // Case 5: Default fallback - show generic denial message
  return (
    <GenericDenialMessage
      featureName={displayName || featureKey}
      reason={denialReason}
    />
  );
};

/**
 * Note: The inline TeaserPage component has been removed.
 * We now use the rich legacy TeaserPage from pages/TeaserPage.tsx
 * which includes all demo components, animations, and teaserContent.ts integration.
 */

/**
 * Higher-order component version of FeatureGate
 */
export const withFeatureFlag = (featureKey: string, options: Partial<FeatureGateProps> = {}) => {
  return function <P extends object>(Component: React.ComponentType<P>) {
    return function FeatureGatedComponent(props: P) {
      return (
        <FeatureGate featureKey={featureKey} {...options}>
          <Component {...props} />
        </FeatureGate>
      );
    };
  };
};

/**
 * Hook-based component for conditional rendering with more control
 */
export const ConditionalFeature: React.FC<{
  featureKey: string;
  children: (props: {
    isEnabled: boolean;
    isLoading: boolean;
    accessResult?: string;
    reason?: string;
    displayName?: string;
    minimumRole?: string;
  }) => React.ReactNode;
}> = ({ featureKey, children }) => {
  const featureData = useFeatureFlag(featureKey);
  return <>{children(featureData)}</>;
};

/**
 * Multiple feature gate - requires ALL features to be enabled
 */
export const MultiFeatureGate: React.FC<{
  featureKeys: string[];
  children: React.ReactNode;
  mode?: 'all' | 'any';
  fallback?: React.ReactNode;
  loadingComponent?: React.ReactNode;
}> = ({ 
  featureKeys, 
  children, 
  mode = 'all', 
  fallback,
  loadingComponent 
}) => {
  const featureStates = featureKeys.map(key => useFeatureFlag(key));
  
  const isLoading = featureStates.some(state => state.isLoading);
  const hasAccess = mode === 'all' 
    ? featureStates.every(state => state.isEnabled)
    : featureStates.some(state => state.isEnabled);
  
  if (isLoading) {
    return loadingComponent ? <>{loadingComponent}</> : null;
  }
  
  if (hasAccess) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {children}
      </motion.div>
    );
  }
  
  return fallback ? <>{fallback}</> : null;
};

// Pre-configured feature gates for common features
export const PortfolioOptimizationGate: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FeatureGate featureKey="portfolio_optimization" showTeaser={true}>
    {children}
  </FeatureGate>
);

export const AdvancedChartsGate: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FeatureGate featureKey="advanced_charts" showTeaser={true}>
    {children}
  </FeatureGate>
);

export const ExportFunctionalityGate: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FeatureGate featureKey="export_functionality" showTeaser={true}>
    {children}
  </FeatureGate>
);

export const AdminControlsGate: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <FeatureGate featureKey="admin_controls" showTeaser={false}>
    {children}
  </FeatureGate>
);

export default FeatureGate;